import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { translateSubtree } from '@theme/utilities';

const selectors = {
  SECTION_ID: 'fn-section-rendering-products-all',
  STORAGE_KEY: 'viewedProducts',
  RESULTS_ID: 'products-rendering-results',
  CAROUSEL_CONTAINER: '.carousel__container',
  CAROUSEL_COMPONENT: 'carousel-component',
  CONTENT_BLOCK: '[id^="content-"]',
  TAB_LABEL: (/** @type {string} */ blockId) => `tab-${blockId}`,
  TAB_RADIO: 'input[type="radio"]',
  TAB_RADIO_BY_NAME: (/** @type {string} */ name) => `input[type="radio"][name="${name}"]`,
};

const MAX_PRODUCTS = 8;

export class RecentlyViewed extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#loadProducts();
  }

  static #getAllProducts() {
    try {
      const parsed = JSON.parse(localStorage.getItem(selectors.STORAGE_KEY) || '{}');

      return Array.isArray(parsed) ? {} : parsed;
    } catch {
      return {};
    }
  }

  // Prepend product to storage, deduplicate, cap at MAX_PRODUCTS, scoped to region
  static addProduct(/** @type {string} */ productId, /** @type {string} */ region) {
    const all = RecentlyViewed.#getAllProducts();
    const regionProducts = (all[region] ?? []).filter(
      (/** @type {string} */ id) => id !== productId,
    );
    regionProducts.unshift(productId);
    all[region] = regionProducts.slice(0, MAX_PRODUCTS + 1);
    localStorage.setItem(selectors.STORAGE_KEY, JSON.stringify(all));
  }

  static getProducts(/** @type {string} */ region) {
    return RecentlyViewed.#getAllProducts()[region] ?? [];
  }

  // Fetch product cards and swap into existing carousel, or remove tab if nothing to show
  async #loadProducts() {
    const markup = await this.#fetchMarkup();
    const results = markup
      ? new DOMParser().parseFromString(markup, 'text/html').getElementById(selectors.RESULTS_ID)
      : null;
    const carouselContainer = results && this.querySelector(selectors.CAROUSEL_CONTAINER);

    if (!carouselContainer) return this.#removeFromDOM();

    carouselContainer.innerHTML = results.innerHTML;
    // Source-locale cards — translate after swap.
    translateSubtree(carouselContainer);
    /** @type {any} */ (this.querySelector(selectors.CAROUSEL_COMPONENT))?.reInit();
  }

  // Remove both the content block and its corresponding tab label
  // If the removed tab was active, activate the next available tab
  #removeFromDOM() {
    const contentBlock = this.closest(selectors.CONTENT_BLOCK);
    if (!contentBlock) return;

    const blockId = contentBlock.id.replace('content-', '');
    const tabLabel = document.getElementById(selectors.TAB_LABEL(blockId));
    const radio = /** @type {HTMLInputElement | null} */ (
      tabLabel?.querySelector(selectors.TAB_RADIO)
    );
    const isChecked = radio?.checked ?? false;
    const radioName = radio?.name;

    tabLabel?.remove();
    contentBlock.remove();

    if (isChecked && radioName) {
      const nextRadio = /** @type {HTMLInputElement | null} */ (
        document.querySelector(selectors.TAB_RADIO_BY_NAME(radioName))
      );
      if (nextRadio) nextRadio.checked = true;
    }
  }

  // Build search URL from stored product IDs and fetch section HTML
  async #fetchMarkup() {
    const region = this.dataset.region ?? '';
    const currentProductId = String(Theme?.product?.id ?? '');
    const viewedProducts = RecentlyViewed.getProducts(region)
      .filter((/** @type {string} */ id) => id !== currentProductId)
      .slice(0, MAX_PRODUCTS);

    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set(
      'q',
      viewedProducts.map((/** @type {string} */ id) => `id:${id}`).join(' OR '),
    );
    url.searchParams.set('type', 'product');
    // Recently viewed shows sold-out on purpose; search's default is merchant-configurable
    // in Search & Discovery and can be set to drop them
    url.searchParams.set('options[unavailable_products]', 'show');

    return sectionRenderer.getSectionHTML(selectors.SECTION_ID, false, url);
  }
}

if (!customElements.get('recently-viewed')) {
  customElements.define('recently-viewed', RecentlyViewed);
}
